// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminDepartamentos (migrado al shell · prompt 0471)
// CRUD table for departments management
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  fetchDepartamentos,
  fetchOrganizaciones,
  fetchPuestos,
  request,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

// ─── Types ───────────────────────────────────────────────────

interface Departamento {
  id: string;
  nombre: string;
  codigo: string;
  slug?: string | null;
  activo?: boolean;
  color?: string | null;
  organizacionId?: string;
  responsableId?: string | null;
  responsable?: { titulo: string } | null;
  padreId?: string | null;
  tipoUnidadOrganizativaId?: string;
  tipoUnidadOrganizativa?: {
    id: string;
    nombre: string;
    nombreCorto: string;
    orden: number;
    color: string | null;
  };
  _count?: {
    personas?: number;
    puestos?: number;
  };
}

interface Organizacion {
  id: string;
  nombre: string;
}

// ─── Iconos SVG inline ───────────────────────────────────────

function EditIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

// ─── Modal ───────────────────────────────────────────────────

function DepartamentoModal({
  departamento,
  organizaciones,
  allDepartamentos,
  onClose,
  onSaved,
}: {
  departamento: Departamento | null;
  organizaciones: Organizacion[];
  allDepartamentos: Departamento[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;

  const isNew = !departamento;
  const [nombre, setNombre] = useState(departamento?.nombre ?? '');
  const [codigo, setCodigo] = useState(departamento?.codigo ?? '');
  const [color, setColor] = useState(departamento?.color ?? '#1F3864');
  const [organizacionId, setOrganizacionId] = useState(departamento?.organizacionId ?? '');
  const [responsableId, setResponsableId] = useState(departamento?.responsableId ?? '');
  const [padreId, setPadreId] = useState(departamento?.padreId ?? '');
  const [tipoUnidadOrganizativaId, setTipoUnidadOrganizativaId] = useState(
    departamento?.tipoUnidadOrganizativaId ?? '',
  );
  const [tiposUnidad, setTiposUnidad] = useState<
    Array<{ id: string; nombre: string; nombreCorto: string; orden: number }>
  >([]);
  const [puestosOrg, setPuestosOrg] = useState<Array<{ id: string; titulo: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    request<{ data: typeof tiposUnidad }>('/tipos-unidad-organizativa')
      .then((r) => setTiposUnidad((r.data ?? []).sort((a, b) => a.orden - b.orden)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!organizacionId) {
      setPuestosOrg([]);
      return;
    }
    fetchPuestos({ organizacionId, porPagina: '100' })
      .then((r) =>
        setPuestosOrg(
          (r.data ?? []).map((p) => ({
            id: p.id,
            titulo: p.titulo,
          })),
        ),
      )
      .catch(() => setPuestosOrg([]));
  }, [organizacionId]);

  const hermanos = allDepartamentos.filter(
    (d) => d.organizacionId === organizacionId && d.id !== departamento?.id,
  );

  const handleSave = async () => {
    if (!nombre.trim() || !organizacionId) {
      setError('Nombre y organizacion son obligatorios');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const body: Record<string, unknown> = {
        nombre: nombre.trim(),
        organizacionId,
      };
      if (codigo.trim()) body.codigo = codigo.trim();
      if (color) body.color = color;
      if (responsableId) body.responsableId = responsableId;
      else body.responsableId = null;
      if (padreId) body.padreId = padreId;
      else body.padreId = null;
      if (tipoUnidadOrganizativaId) body.tipoUnidadOrganizativaId = tipoUnidadOrganizativaId;
      else body.tipoUnidadOrganizativaId = null;

      if (isNew) {
        await request('/departamentos', { method: 'POST', body: JSON.stringify(body) });
      } else {
        await request(`/departamentos/${departamento.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: '3px solid #1F3864',
          width: 420,
          maxWidth: '90vw',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0E243E', margin: '0 0 20px' }}>
          {isNew ? 'Nuevo departamento' : 'Editar departamento'}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div
              style={{
                background: '#FEF2F2',
                color: '#DC2626',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Nombre *
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Codigo
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder={
                (adm?.departamentosPlaceholders as Record<string, string> | undefined)?.codigo ??
                'Ej: DEP-001'
              }
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Organizacion *
            <select
              value={organizacionId}
              onChange={(e) => setOrganizacionId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                background: '#fff',
              }}
            >
              <option value="">Seleccionar...</option>
              {organizaciones.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nombre}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Color
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <input
                type="color"
                value={color ?? '#1F3864'}
                onChange={(e) => setColor(e.target.value)}
                style={{
                  width: 36,
                  height: 28,
                  border: '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: color ?? '#1F3864',
                }}
              />
              <span style={{ fontSize: 11, color: '#94A3B8' }}>{color}</span>
            </div>
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Puesto responsable
            <select
              value={responsableId}
              onChange={(e) => setResponsableId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                background: '#fff',
              }}
            >
              <option value="">Sin responsable</option>
              {puestosOrg.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.titulo}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            Padre
            <select
              value={padreId}
              onChange={(e) => setPadreId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                background: '#fff',
              }}
            >
              <option value="">Sin padre (raiz)</option>
              {hermanos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
            {((adm as Record<string, unknown> | undefined)?.tiposUnidad as string) ??
              'Tipo de unidad'}
            <select
              value={tipoUnidadOrganizativaId}
              onChange={(e) => setTipoUnidadOrganizativaId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '8px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'inherit',
                background: '#fff',
              }}
            >
              <option value="">-- Sin tipo --</option>
              {tiposUnidad.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre} ({t.nombreCorto})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            borderTop: '1px solid #E5E7EB',
            paddingTop: 16,
            marginTop: 16,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              background: '#fff',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '7px 16px',
              border: 'none',
              borderRadius: 6,
              background: '#2F5496',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────

export function AdminDepartamentos() {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const tooltipsObj = (adm?.tooltips as Record<string, string> | undefined) ?? {};

  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [organizaciones, setOrganizaciones] = useState<Organizacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modalDep, setModalDep] = useState<Departamento | null | 'new'>(null);
  const [toast, setToast] = useState('');

  const orgMap = new Map(organizaciones.map((o) => [o.id, o.nombre]));

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dRes, oRes] = await Promise.all([
        fetchDepartamentos({ porPagina: '100' }),
        fetchOrganizaciones(),
      ]);
      setDepartamentos(dRes.data ?? []);
      setOrganizaciones(
        (oRes.data ?? []).map((o) => ({
          id: o.id,
          nombre: o.nombre,
        })),
      );
    } catch {
      setDepartamentos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const q = search.toLowerCase().trim();
  const filtered = departamentos
    .filter((d) => (mostrarInactivos ? true : d.activo !== false))
    .filter((d) =>
      q
        ? d.nombre.toLowerCase().includes(q) ||
          (d.codigo ?? '').toLowerCase().includes(q) ||
          (d.slug ?? '').toLowerCase().includes(q)
        : true,
    );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const columns: AdminTableColumn<Departamento>[] = [
    {
      key: 'color',
      label: '',
      width: 36,
      render: (d) => (
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: d.color ?? '#1F3864',
          }}
        />
      ),
    },
    {
      key: 'codigo',
      label: lbl.colCodigo ?? 'Código',
      mono: true,
      sortable: true,
      sortKey: (d) => d.codigo ?? '',
      render: (d) => (
        <span
          title={d.slug ? `slug: ${d.slug}` : undefined}
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#2F5496' }}
        >
          {d.codigo || '—'}
          {d.slug ? <span style={{ color: '#9CA3AF', marginLeft: 4 }}>({d.slug})</span> : null}
        </span>
      ),
    },
    {
      key: 'nombre',
      label: lbl.colNombre ?? 'Nombre',
      sortable: true,
      sortKey: (d) => d.nombre,
      render: (d) => <span style={{ fontWeight: 600 }}>{d.nombre}</span>,
    },
    {
      key: 'organizacion',
      label: lbl.colOrganizacion ?? 'Organización',
      sortable: true,
      sortKey: (d) => (d.organizacionId ? (orgMap.get(d.organizacionId) ?? '') : ''),
      render: (d) => (
        <span style={{ color: '#64748B' }}>
          {d.organizacionId ? (orgMap.get(d.organizacionId) ?? '—') : '—'}
        </span>
      ),
    },
    {
      key: 'tipo',
      label:
        ((adm as Record<string, unknown> | undefined)?.tiposUnidad as string) ?? lbl.tipo ?? 'Tipo',
      render: (d) => (
        <span style={{ color: '#64748B', fontSize: 12 }}>
          {d.tipoUnidadOrganizativa?.nombreCorto ?? '—'}
        </span>
      ),
    },
    {
      key: 'responsable',
      label: lbl.responsable ?? 'Responsable',
      render: (d) => (
        <span style={{ color: '#64748B', fontSize: 12 }}>{d.responsable?.titulo ?? '—'}</span>
      ),
    },
    {
      key: 'personas',
      label: lbl.personas ?? 'Personas',
      align: 'center',
      render: (d) => (
        <span style={{ color: '#64748B' }}>
          {d._count?.personas != null ? d._count.personas : '—'}
        </span>
      ),
    },
    {
      key: 'puestos',
      label: lbl.puestos ?? 'Puestos',
      align: 'center',
      render: (d) => (
        <span style={{ color: '#64748B' }}>
          {d._count?.puestos != null ? d._count.puestos : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={lbl.departamentos ?? 'Departamentos'}
        infoTooltip={{
          title: lbl.departamentos ?? 'Departamentos',
          text: 'Unidades organizativas en las que se estructura la organización.\n\nCada departamento pertenece a una organización, puede tener un departamento padre (jerarquía) y un puesto responsable.\n\nEl tipo de unidad (configurable por tenant) permite clasificar departamentos en Direcciones, Áreas, Unidades, etc.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: lbl.search ?? 'Buscar...',
        }}
        onNew={{
          label: lbl.newDepartment ?? 'Nuevo departamento',
          onClick: () => setModalDep('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {lbl.mostrarInactivos ?? 'Mostrar inactivos'}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(d) => d.id}
        onRowClick={(d) => setModalDep(d)}
        loading={loading}
        loadingMessage={((t.common as Record<string, unknown>)?.cargando as string) ?? 'Cargando…'}
        emptyMessage={lbl.noResults ?? 'Sin resultados'}
        actions={(d) => (
          <AdminActionButton
            icon={<EditIcon />}
            label={tooltipsObj.editar ?? 'Editar'}
            onClick={() => setModalDep(d)}
          />
        )}
        footer={
          !loading ? (
            <span>
              {filtered.length} de {departamentos.length}{' '}
              {lbl.departamentosContador ?? 'departamentos'}
            </span>
          ) : undefined
        }
      />

      {modalDep !== null && (
        <DepartamentoModal
          departamento={modalDep === 'new' ? null : modalDep}
          organizaciones={organizaciones}
          allDepartamentos={departamentos}
          onClose={() => setModalDep(null)}
          onSaved={() => {
            setModalDep(null);
            loadData();
            showToast(lbl.saved ?? 'Guardado correctamente');
          }}
        />
      )}
    </div>
  );
}
